import { Flex, Grid } from '@chakra-ui/react'
import { datadogRum } from '@datadog/browser-rum'
import styled from '@emotion/styled'
import {
  ConfigurationDetail,
  DatadogSimpleReadingListRun,
  EncodingValidationRequest,
  Products,
  SimpleReadingList,
  SimpleReadingLists,
  TmrProduct,
  TmrTag
} from 'core/api'
import { Box, Button, EncodingReading, FullLoadingLayer, Page, ProductInfoBox } from 'core/components'
import {
  AppStore,
  OperationReadingProps,
  RemoteOperation,
  RfidReader,
  SimpleReadingListOperationConfig
} from 'core/shared'
import { __, T } from 'core/shared/i18n'
import {
  getTimeoutValidateTags,
  isModalError,
  showToast,
  showToastError,
  sleep,
  uuid,
  CUSTOM_COMMANDS_OPERATION_TYPE
} from 'core/shared/utils'
import { OperationEncodingTagType } from 'core/types'
import { NfcAuth, ValidateWithProductInfoResponse } from 'custom/louis-vuitton/api/NfcAuth'
import { IdentifierBox } from 'custom/louis-vuitton/components'
import moment from 'moment'
import { Component } from 'react'
import { EncodingExtensions } from 'stylewhere/extensions'

interface State {
  simpleReadingList?: SimpleReadingList
  identifiers: ConfigurationDetail[]
  product?: TmrProduct
  itemAttributes?: ValidateWithProductInfoResponse
  antennaOn: boolean
  processing: boolean
  starting: boolean
}

export default class OneIDTable extends Component<OperationReadingProps<State>, State> {
  operation = RemoteOperation.getOperationConfig<SimpleReadingListOperationConfig>('nfc-auth')
  timer: NodeJS.Timeout | null = null
  isModal = false
  isDecodeNfc = false
  printerCode = ''

  datadogSession: DatadogSimpleReadingListRun | undefined = undefined

  state: State = {
    identifiers: [],
    antennaOn: false,
    processing: false,
    starting: false
  }

  componentDidMount() {
    this._init()
  }

  _init = async () => {
    this.isModal = isModalError(this.operation)
    this.startSimpleReadingListAndStartReader()
  }

  initializeDataDogSession = (additionalData?: any) => {
    const isActive = AppStore.isDatadogActive()
    if (isActive) {
      this.datadogSession = {
        runId: uuid().toString(),
        operationCode: this.operation.code,
        nfcRead: [],
        startTime: new Date(),
        productInfos: [],
        errors: [],
        additionalInfo: additionalData
      }
      datadogRum.addAction('nfcAuthStartOperation', this.datadogSession)
    }
  }

  startSimpleReadingListAndStartReader = async (manual = false) => {
    const workstationId = AppStore.defaultWorkstation.id
    const postData = {
      workstationId,
      operationPlaceId: AppStore.defaultWorkstation.placeId,
      operationId: this.operation.id
    }
    try {
      this.initializeDataDogSession(postData)
      const res = await SimpleReadingLists.start(postData)
      if (this.operation.autostartAntenna || manual) {
        this.setState({ starting: true, simpleReadingList: res }, this.startReader)
      } else {
        this.setState({ simpleReadingList: res })
      }
    } catch (error) {
      if (this.datadogSession) {
        this.datadogSession.errors.push((error as any).message ?? __(T.error.error))
        datadogRum.addAction('nfcAuthStartOperationError', this.datadogSession)
      }
      showToastError((error as any).message ?? __(T.error.error), __(T.error.error), this.isModal)
    }
  }

  startReader = async () => {
    try {
      await RfidReader.initialize(CUSTOM_COMMANDS_OPERATION_TYPE)
      RfidReader.setOnTagReadCallback(this.onTagRead)
      RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
      RfidReader.setAutomaticStopTime(this.operation.autostopAntennaTimeout)
      RfidReader.onStartCallback = this.onStartCallback
      RfidReader.onStopCallback = this.onStopCallback
      await RfidReader.start({
        restart: false,
        tagTypeToCheck: 'NFC',
        customCommandsOperationType: CUSTOM_COMMANDS_OPERATION_TYPE
      })
    } catch (error) {
      showToastError((error as any).message ?? __(T.error.rfid_reader_initialization), __(T.error.error), this.isModal)
    }
  }

  onStartCallback = () => {
    this.setState({ antennaOn: true, starting: false })
  }

  onStopCallback = (automatic = false) => {
    this.setState({ antennaOn: false, starting: false })
  }

  componentWillUnmount = () => {
    this.setState({ processing: false })
    RfidReader.onStartCallback = undefined
    RfidReader.onStopCallback = undefined
    void this.stopAntenna()
  }

  /** Returns true if the read should be aborted (NFC auth error). */
  handleNfcAuthError = (encodingValidation: EncodingValidationRequest): boolean => {
    const nfcError = encodingValidation.identifiers.some(
      (id) => id.identifierType === 'NFC_TAG' && id._status === 'ERROR'
    )
    if (!nfcError) return false

    this.isDecodeNfc = false
    this.setState({ processing: false, itemAttributes: (encodingValidation as any).itemAttributes })
    if (this.datadogSession) {
      const errorTag = encodingValidation.identifiers.find(
        (id) => id.identifierType === 'NFC_TAG' && id._status === 'ERROR'
      )
      this.datadogSession.errors.push({
        error: errorTag._error ?? errorTag._errorMessage ?? errorTag._status ?? errorTag?.status ?? errorTag._errorCode
      })
      datadogRum.addAction('nfcAuthTagReadError', this.datadogSession)
    }
    return true
  }

  /** Returns true if the read should be aborted (NFC collision). */
  handleNfcCollision = (identifiers: ConfigurationDetail[]): boolean => {
    const nfcCollision = identifiers.some((id) => id.identifierType === 'NFC_TAG' && id._status === 'NFC_COLLISION')
    if (!nfcCollision) return false

    this.isDecodeNfc = false
    this.setState({ processing: false })
    if (this.datadogSession && !this.datadogSession.errors.some((e) => e.error === 'nfcAuthTagCollision')) {
      this.datadogSession.errors.push({ error: 'nfcAuthTagCollision' })
      datadogRum.addAction('nfcAuthTagCollision', this.datadogSession)
    }
    return true
  }

  getProductFromItemAttributes = (encodingValidation: EncodingValidationRequest) => {
    const itemAttributes = (encodingValidation as any).itemAttributes
    const productData: any = itemAttributes?.verificationData?.additionalInformation ?? {}
    const product: Partial<TmrProduct> | undefined = itemAttributes?.verificationData?.additionalInformation
      ? { code: productData.sku, description: productData.description }
      : undefined
    return { product, productData }
  }

  onTagRead = async (tag: TmrTag, antennaId?: string) => {
    if (!tag.uid) return
    await sleep(Math.random() * 500)

    const { identifiers } = this.state

    if (tag.uid && this.isDecodeNfc) {
      RfidReader.removeTags([tag.uid])
      return
    }

    try {
      this.isDecodeNfc = true
      this.stopTimer()
      this.setState({ processing: true })
      if (!identifiers.some((id) => id.identifierType === 'NFC_TAG')) {
        identifiers.push({
          identifierType: 'NFC_TAG',
          _status: 'PROCESSING'
        })
      }
      const encodingValidation = { identifiers, operationId: this.operation.id }
      await EncodingExtensions.onTagRead(
        encodingValidation,
        tag,
        {
          ...this.operation,
          type: 'ENCODING',
          attributes: { ...this.operation.attributes, isOneIDTable: 'true' } as any
        },
        antennaId
      )
      RfidReader.stop({ customCommandsOperationType: CUSTOM_COMMANDS_OPERATION_TYPE })

      if (this.handleNfcAuthError(encodingValidation)) return
      if (this.handleNfcCollision(identifiers)) return

      const { product, productData } = this.getProductFromItemAttributes(encodingValidation)

      if (this.datadogSession) {
        this.datadogSession.nfcRead.push({
          nfcRead: tag.uid,
          offsetMs: Date.now() - this.datadogSession.startTime.getTime()
        })
        this.datadogSession.productInfos.push({
          [tag.uid]: productData
        })
        datadogRum.addAction('nfcAuthTagRead', this.datadogSession)
      }

      this.setState(
        {
          identifiers: identifiers
            .filter((id) => id.identifierType === 'NFC_TAG')
            .map((id) => ({ ...id, _status: 'CONFIRMED' })),
          processing: false,
          itemAttributes: (encodingValidation as any).itemAttributes,
          product: product as TmrProduct
        },
        () => {
          this.isDecodeNfc = false
          this.onTagReadTimer()
        }
      )
    } catch (error) {
      console.error(error)
      this.setState({ processing: false })
      if (this.datadogSession) {
        this.datadogSession.errors.push((error as any).message ?? __(T.error.error))
        datadogRum.addAction('nfcAuthTagReadError', this.datadogSession)
      }
      showToastError((error as any).message ?? __(T.error.error), __(T.error.error), this.isModal)
    } finally {
      this.isDecodeNfc = false
    }
  }

  onTagReadTimer = async () => {
    const timeoutValidateTags = getTimeoutValidateTags(this.operation)
    if (timeoutValidateTags !== undefined && timeoutValidateTags > 0) {
      this.timer = setTimeout(async () => this.updateReadings(), timeoutValidateTags)
    } else {
      this.updateReadings()
    }
  }

  updateReadings = async () => {
    const { identifiers, simpleReadingList } = this.state
    if (!identifiers || identifiers.length === 0) {
      this.setState({ processing: false })
      return
    }
    const updateReq = {
      operationId: this.operation.id,
      simpleReadingListId: simpleReadingList.id,
      updateAction: 'INSERT',
      codes: identifiers
        .filter((e) => !!e.code)
        .map((e) => {
          return { code: e.code, identifierType: e.identifierType }
        })
    }
    try {
      // update readings
      await SimpleReadingLists.updateReadings(updateReq)

      const confirmReq = {
        operationId: this.operation.id,
        simpleReadingListId: simpleReadingList.id,
        attributes: {}
      }
      await SimpleReadingLists.confirm(confirmReq)

      if (this.datadogSession) {
        this.datadogSession.endTime = new Date()
        this.datadogSession.additionalInfo = {
          ...this.datadogSession.additionalInfo,
          ...updateReq
        }
        datadogRum.addAction('nfcAuthConfirmOperation', this.datadogSession)
      }

      this.fetchProductImage()
    } catch (error) {
      console.error(error)
      if (this.datadogSession) {
        this.datadogSession.additionalInfo = {
          ...this.datadogSession.additionalInfo,
          ...updateReq
        }
        this.datadogSession.errors.push((error as any).message ?? __(T.error.error))
        datadogRum.addAction('nfcAuthConfirmOperationError', this.datadogSession)
      }
      showToastError((error as any).message ?? __(T.error.error), __(T.error.error), this.isModal)
      this.setState({ processing: false })
    }
  }

  fetchProductImage = async () => {
    const { product } = this.state
    try {
      this.stopTimer()
      if (!product) return
      const _product = await Products.searchCodes([product.code])
      if (!_product?.content || _product.content.length === 0) return
      this.setState({
        product: _product.content[0]
      })
    } catch (error) {
      console.error(error)
    }
  }

  stopTimer = () => {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  clearReads = async () => {
    this.stopTimer()

    this.setState(
      {
        identifiers: [],
        simpleReadingList: undefined,
        itemAttributes: undefined,
        product: undefined,
        processing: false
      },
      () => this.startSimpleReadingListAndStartReader(true)
    )
  }

  getIdentifierBoxColor = () => {
    const { itemAttributes } = this.state
    switch (itemAttributes?.statusCode) {
      case 'F001':
        return '#75EBA8'
      case 'F005':
        return 'darkOrange'
      case 'F006':
        return 'lightGray'
      case 'F008':
        return '#75EBA8'
      default:
        return undefined
    }
  }

  renderIdentifierBox = (_identifier: ConfigurationDetail, index: any) => {
    if (_identifier.identifierType === 'UHF_TAG') return null
    const isSerial = _identifier.identifierType === 'SIMPLE_ITEM_IDENTIFIER'
    return (
      <IdentifierBox
        hideCode={_identifier.identifierType === 'NFC_TAG'}
        renderSimpleItemIdentifierAsNormalIdentifier
        hideStatusLabel={isSerial}
        identifier={_identifier}
        style={{ backgroundColor: isSerial ? undefined : this.getIdentifierBoxColor() }}
        key={index}
      />
    )
  }

  startAntenna = async (tagTypeInput?: OperationEncodingTagType) => {
    this.startSimpleReadingListAndStartReader(true)
  }

  /* stop antenna if reading */
  stopAntenna = async () => {
    if (RfidReader.isReading()) {
      this.setState({ starting: true })
      await RfidReader.stop({ customCommandsOperationType: CUSTOM_COMMANDS_OPERATION_TYPE })
    }
  }

  getLabelFromStatusCode = (statusCode: string) => {
    switch (statusCode) {
      case 'F001':
        return `${statusCode} - ${__(T.custom.product_is_authentic)}`
      case 'F005':
        return `${statusCode} - ${__(T.custom.product_is_authentic)}`
      case 'F006':
        return `${statusCode} - ${__(T.custom.product_is_authentic)}`
      case 'F008':
        return `${statusCode} - ${__(T.custom.product_is_authentic)}`
      case 'T999':
        return undefined
      default:
        return undefined
    }
  }

  getCaptionFromStatusCode = (statusCode: string) => {
    switch (statusCode) {
      case 'F001':
        return undefined
      case 'F005':
        return __(T.custom.not_sellable)
      case 'F006':
        return `${statusCode} ${__(T.custom.stock_product)}`
      case 'F008':
        return undefined
      default:
        return undefined
    }
  }

  itemAttributesToHide = [
    'sku',
    'description',
    'citesFlag',
    'status',
    'blockchainID',
    'supplierCode',
    'productName',
    'returnCode'
  ]

  hasDisplayableAdditionalInformationValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim() !== ''
    return !!value
  }

  sendReprint = async () => {
    const { itemAttributes, simpleReadingList } = this.state
    const { additionalInformation } = itemAttributes.verificationData
    try {
      if (!itemAttributes) throw new Error('Item attributes not found')
      if (!simpleReadingList) throw new Error('Simple reading list not found')
      additionalInformation.printerCode = this.printerCode
      await SimpleReadingLists.executeAction(this.operation, simpleReadingList.id, 'REPRINT', {
        attributes: additionalInformation
      })
      if (this.datadogSession) {
        this.datadogSession.additionalInfo = {
          ...this.datadogSession.additionalInfo,
          ...additionalInformation
        }
        datadogRum.addAction('nfcAuthReprintOperation', this.datadogSession)
      }
      showToast({
        title: __(T.misc.success),
        description: __(T.custom.reprint_sent),
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      console.error(error)
      if (this.datadogSession) {
        this.datadogSession.additionalInfo = {
          ...this.datadogSession.additionalInfo,
          ...additionalInformation
        }
        this.datadogSession.errors.push((error as any).message ?? __(T.error.error))
        datadogRum.addAction('nfcAuthReprintOperationError', this.datadogSession)
      }
      showToastError((error as any).message ?? __(T.error.error), __(T.error.error), this.isModal)
    }
  }

  viewCitesDocument = async () => {
    const { itemAttributes } = this.state
    if (!itemAttributes?.verificationData?.additionalInformation?.serialNumber) {
      showToastError(__(T.custom.INVALID_SERIAL), __(T.error.error), this.isModal)
      return
    }

    const serialNumber = itemAttributes.verificationData.additionalInformation.serialNumber

    const newTab = window.open('about:blank', '_blank')
    const doc = newTab.document
    Object.assign(doc.body.style, {
      display: 'flex',
      height: '100vh',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'sans-serif',
      margin: 0
    })

    const loadingText = doc.createElement('h2')
    loadingText.textContent = __(T.misc.pleaseWait)
    doc.body.appendChild(loadingText)

    if (this.datadogSession) {
      if (!this.datadogSession.additionalInfo) this.datadogSession.additionalInfo = {}
      this.datadogSession.additionalInfo = {
        ...this.datadogSession.additionalInfo,
        nfcAuthCitesCertificateFetch: {
          serial: serialNumber,
          offsetMs: Date.now() - this.datadogSession.startTime.getTime()
        }
      }
      datadogRum.addAction('nfcAuthCitesCertificateFetch', this.datadogSession)
    }

    try {
      const pdf = await NfcAuth.fetchCitesCertificate(serialNumber)
      const url = URL.createObjectURL(pdf)
      newTab.location.href = url
    } catch (err: any) {
      showToastError(err.message, __(T.error.error), this.isModal)
      loadingText.textContent = err.message
      loadingText.style.color = 'red'
      console.error(err)
      if (this.datadogSession) {
        this.datadogSession.errors.push({
          error: 'nfcAuthCitesCertificateFetchError',
          details: err.message
        })
        datadogRum.addAction('nfcAuthCitesCertificateFetchError', this.datadogSession)
      }
    }
  }

  render() {
    const { identifiers, product, antennaOn, processing, starting, itemAttributes } = this.state
    const statusLabel = itemAttributes ? this.getLabelFromStatusCode(itemAttributes.statusCode) : undefined
    const statusCaption = itemAttributes ? this.getCaptionFromStatusCode(itemAttributes.statusCode) : undefined
    const serial = itemAttributes?.verificationData?.additionalInformation?.serialNumber
    const reprintAction = this.operation.actions?.find((action) => action.actionCode === 'REPRINT')
    const showReprintButton =
      !!reprintAction &&
      (AppStore.isAdmin() ||
        (AppStore.loggedUser.roles ?? []).some((userRole) =>
          (reprintAction.visibleForRoles ?? []).includes(userRole.id)
        ))
    const showCitesButton =
      itemAttributes?.verificationData?.additionalInformation?.citesFlag === 'Y' &&
      !!product?.attributes?.materialGroup?.value &&
      product.attributes.materialGroup.value.toUpperCase() !== 'Z04'

    return (
      <Page
        title={this.operation.description}
        enableEmulation
        activeAntennaSettings
        userSettings={{
          operation: this.operation,
          settings: this.operation.userSettings ?? []
        }}
        onUserSettingChanged={(settingName, value) => {
          if (settingName === 'printerCode') {
            this.printerCode = value as string
          }
        }}
      >
        {identifiers.length > 0 && (
          <Page.Sidebar width={500}>
            <Box flex justify={'space-between'}>
              <Box>
                {!!product && (
                  <Box style={{ marginBottom: '10px' }} key={product.id}>
                    <ProductInfoBox paddingCard={'20px 20px 20px 20px'} product={product} />
                  </Box>
                )}
                {statusLabel && (
                  <CardContainer padding={''} shadow={''}>
                    <Title>{statusLabel}</Title>
                  </CardContainer>
                )}
                {statusCaption && (
                  <CardContainer padding={''} shadow={''}>
                    <Title>{__(T.custom.store_action)}</Title>
                    <Caption>{statusCaption}</Caption>
                  </CardContainer>
                )}
              </Box>
              {!product && <Box flex />}
              {identifiers.length > 0 && (
                <Button
                  variant="primary"
                  title={__(T.misc.clear_and_restart)}
                  onClick={() => this.clearReads()}
                  radius={100}
                />
              )}
            </Box>
          </Page.Sidebar>
        )}
        <Page.Content notBoxed>
          <Box>
            {identifiers.length > 0 && (
              <Box flex row justify={'space-between'} style={{ flexWrap: 'wrap', paddingBottom: 2 }}>
                {identifiers.map((identifier, index) => this.renderIdentifierBox(identifier, index))}
              </Box>
            )}
          </Box>
          <Box flex={identifiers.length}>
            {itemAttributes?.verificationData?.additionalInformation && (
              <Grid
                templateColumns={`repeat(2, 1fr)`}
                gap={4}
                style={{
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box',
                  borderRadius: '10px',
                  padding: '20px',
                  margin: '2px',
                  marginBottom: '10px',
                  boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.15)'
                }}
              >
                {Object.entries(itemAttributes.verificationData.additionalInformation)
                  .filter(
                    ([key, value]) =>
                      !this.itemAttributesToHide.includes(key) && this.hasDisplayableAdditionalInformationValue(value)
                  )
                  .map((data) => {
                    const key = data[0]
                    let value = data[1]
                    if (key === 'pairingDate' && value) {
                      value = moment(value).format('DD/MM/YYYY')
                    }
                    const translatedKey = T.custom[key] ? __(T.custom[key]) : key
                    return (
                      <Flex key={key} direction={'row'} alignItems={'center'} style={{ gap: 8 }} borderBottomWidth={1}>
                        <Caption style={{ fontWeight: 'bold' }}>{`${translatedKey}:`}</Caption>
                        <Caption>{value ? value.toString() : 'N/A'}</Caption>
                      </Flex>
                    )
                  })}
              </Grid>
            )}
          </Box>
          <Box row>
            {identifiers.length > 0 && serial && showReprintButton && (
              <Button
                style={{ ...s.flex, ...s.mx10 }}
                variant="primary"
                title={__(T.custom.reprint)}
                onClick={() => this.sendReprint()}
              />
            )}
            {identifiers.length > 0 && serial && showCitesButton && (
              <Button
                style={{ ...s.flex, ...s.mx10 }}
                variant="primary"
                title={__(T.custom.viewCitesDocument)}
                onClick={this.viewCitesDocument}
              />
            )}
          </Box>
          {identifiers.length === 0 && (
            <EncodingReading
              starting={starting}
              startAntenna={this.startAntenna}
              antennaOn={antennaOn}
              antennaTagType={'NFC'}
              message={antennaOn ? __(T.messages.bring_an_item_to_antenna) : __(T.misc.waiting)}
            />
          )}
        </Page.Content>
        {processing && <FullLoadingLayer message={__(T.messages.nfcDecryption)} />}
      </Page>
    )
  }
}

const CardContainer = styled(Box)<{ padding: string; shadow: string }>`
  background: #ffffff;
  box-sizing: border-box;
  border-radius: 10px;
  padding: ${({ padding }) => padding || '20px;'};
  margin: 2px;
  margin-bottom: 10px;
  box-shadow: ${({ shadow }) => shadow || '0px 1px 4px rgba(0, 0, 0, 0.15);'};
`

const Title = styled.div`
  font-size: 26px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #000000;
`
const Caption = styled.div`
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #444444;
`

const s = {
  flex: {
    flex: 1
  },
  mx10: {
    marginLeft: 5,
    marginRight: 5
  }
}
